"use client";

import dynamic from "next/dynamic";
import { createContext, useContext, useState, type FocusEvent, type PointerEvent } from "react";

import {
  RASHI_CHECK_FIELD_IDS,
  RASHI_CHECK_FORM,
  RASHI_CHECK_FRAME,
} from "@/components/rashi/rashi-check-form";
import BirthToolFormShell, {
  type BirthToolFormShellProps,
} from "@/components/tool-shell/BirthToolFormShell";

import {
  TRANSITS_MOON_CHECK_HI_FORM,
  TRANSITS_MOON_CHECK_HI_RESERVE,
  TRANSITS_MOON_CHECK_RESERVE,
} from "./transits-moon-check-copy";

// The /transits/ and /hi/transits/ check's one boundary, the daily rashifal
// check's pattern (RashifalCheckLazy): the hubs' section imports only this
// module, and it owns the one dynamic() to the island, which serves both
// locales. The form, place search, kundali client, verdict, answers and
// analytics load with the island; the first load gains only this file, the
// shell and the two locales' form props. The hubs shipped no client code of
// their own before this (TransitNowTable is server HTML).
//
// Activation is element-scoped and local: no window listener and no timer, so
// a reader scrolling the table never downloads the island and the deploy's
// body pointerdown smoke loads nothing; a press, focus or submit on the shell
// does.
//
// The slot reserves the locale's tallest state and every state fills it, so
// neither shell -> form nor a slow engine reply -> answer (or error) moves the
// page below. The form is the rashi check's in English and the daily check's
// card in Hinglish, so its tallest states are theirs; the answers here are
// shorter. Natural heights measured 2026-10-15 (next dev + Playwright on
// /transits/ and /hi/transits/, POST /kundali mocked, 7 widths from 320 to
// 1280):
// - English: the shell is 436 at 320-390 px wide and 420 above; the form 436,
//   420 from 414 and 362 from 640. The tallest state is the engine's place
//   error after a typed place nobody picked: 560 at 320-390, 528 at 414, 510
//   at 639 and 452 from 640; an engine failure is 522 at 320 and 430 from
//   640. The tallest answer (Vrishchik with its edge note) is 462 at 320, 399
//   at 390-414 and 393 from 640. Hence 600 px on phones and 500 px from the
//   sm breakpoint, as on the rashi pages.
// - Hindi: the shell and form are 436 below 640 px wide and 420 / 378 from
//   640. The tallest state is the engine failure: 522 below 639 and 446 from
//   640. The answers reach 410 (Vrishchik with its edge note, at 320) and 344
//   from 640. Hence 560 px on phones and 480 px from the sm breakpoint.
// With them the slot held its height in every state, the section's bottom
// never moved, the submit stayed put (527/419 px from the slot's top in
// English, 487/399 in Hindi) before, during and after the press, and no
// layout shift counted (one English cell showed 0.0101 from the site's mobile
// bottom bar at load, before any input; 0 in three reruns). The Hindi hub's
// 20 px gutter is why its submit reads "राशि जाँचें": the daily
// check's longer label wrapped at 320 and moved the submit by 20 px when the
// one-line loading label replaced it.
// translate="no" covers the shell, form and answer, which re-render on "check
// another" (tests/translate-safe-islands.test.ts).

type CheckLanguage = "en" | "hi";

/** Each locale's shell (the live form's words) and slot floor. */
const SLOT: Readonly<Record<CheckLanguage, { shell: BirthToolFormShellProps; reserve: string }>> = {
  en: {
    shell: { ...RASHI_CHECK_FORM, className: RASHI_CHECK_FRAME },
    reserve: TRANSITS_MOON_CHECK_RESERVE,
  },
  hi: {
    shell: { ...TRANSITS_MOON_CHECK_HI_FORM, className: RASHI_CHECK_FRAME },
    reserve: TRANSITS_MOON_CHECK_HI_RESERVE,
  },
};

/**
 * The shell's props for this page's language. dynamic()'s loading placeholder
 * receives no props from the page, so the slot hands them down through
 * context (the /yoga/<slug>/ check's ShellProps).
 */
const ShellProps = createContext<BirthToolFormShellProps>(SLOT.en.shell);

function LoadingShell() {
  return <BirthToolFormShell {...useContext(ShellProps)} />;
}

const TransitsMoonCheck = dynamic(() => import("./TransitsMoonCheck"), {
  ssr: false,
  loading: () => <LoadingShell />,
});

/** BirthToolFormShell input names -> the real form's input ids. */
const SHELL_FIELD_IDS: Readonly<Record<string, string>> = {
  dob: RASHI_CHECK_FIELD_IDS.date,
  tob: RASHI_CHECK_FIELD_IDS.time,
  place: RASHI_CHECK_FIELD_IDS.place,
};

function formFieldId(target: EventTarget | null): string {
  const name = target instanceof Element ? target.getAttribute("name") : null;
  return (name && SHELL_FIELD_IDS[name]) || RASHI_CHECK_FIELD_IDS.date;
}

export default function TransitsMoonCheckLazy({ language }: { language: CheckLanguage }) {
  const [active, setActive] = useState(false);
  const [focusFieldId, setFocusFieldId] = useState<string | null>(null);
  const slot = SLOT[language];

  // The shell's own onActivate cannot tell a keyboard focus from a touch, so
  // the slot reads the intent in the capture phase: focus follows the visitor
  // into the real form after a keyboard focus, a mouse press or a submit, and
  // never after a touch, which may be the start of a scroll.
  const shellIntent = active
    ? {}
    : {
        onPointerDownCapture: (event: PointerEvent<HTMLDivElement>) =>
          setFocusFieldId(event.pointerType === "mouse" ? formFieldId(event.target) : null),
        onFocusCapture: (event: FocusEvent<HTMLDivElement>) =>
          setFocusFieldId(formFieldId(event.target)),
        onSubmitCapture: () => setFocusFieldId(RASHI_CHECK_FIELD_IDS.date),
      };

  return (
    <div
      translate="no"
      data-transits-moon-check-slot=""
      className={`flex flex-col ${slot.reserve}`}
      {...shellIntent}
    >
      {active ? (
        <ShellProps.Provider value={slot.shell}>
          <TransitsMoonCheck language={language} focusFieldId={focusFieldId} />
        </ShellProps.Provider>
      ) : (
        <BirthToolFormShell {...slot.shell} onActivate={() => setActive(true)} />
      )}
    </div>
  );
}
